import { useAuthStore } from '@/auth/authStore';
import { Perm } from '@/auth/permissions';
import { useModuleStore } from '@/core/modules/moduleStore';
import { Modules } from '@/core/modules/moduleKeys';
import { useMyAccess } from './api';
import { useFacilityStore } from './facilityStore';

/**
 * The facility actions the person holds where they are working (docs §301e), for the menu. Asked only where Health is on and the person may use it,
 * and again whenever they choose another facility. Empty until a facility is chosen, so the menu then offers only what needs no role.
 */
export function useHealthNavActions(): ReadonlySet<string> {
  const on = useModuleStore((s) => s.enabled[Modules.Health] === true);
  const uses = useAuthStore((s) => s.hasPermission(Perm.Health.Use));
  const facilityId = useFacilityStore((s) => s.facilityId);
  const { data } = useMyAccess(facilityId, on && uses);
  return new Set(data?.actions ?? []);
}

/**
 * For a page: whether the person holds any of the actions at the facility they work at (docs §301e), so a page offers only the panels their
 * role can use instead of loading one the server refuses. False until the access is known.
 */
export function useHealthCan(): (...actions: string[]) => boolean {
  const held = useHealthNavActions();
  return (...actions) => actions.some((a) => held.has(a));
}

/** Who opens a clinical record, as the server's HealthClinicalAccess.Readers says: those treating the patient. A clerk or cashier sees demographics only. */
export const CLINICAL_READERS = ['clinical.consult', 'ward.care', 'visit.triage', 'pharmacy.verify', 'pharmacy.dispense'];

/** The Health roles the person holds where they work, for the few screens a role rather than an action opens (the national picture is government oversight's). */
export function useHealthRoles(): ReadonlySet<string> {
  const on = useModuleStore((s) => s.enabled[Modules.Health] === true);
  const uses = useAuthStore((s) => s.hasPermission(Perm.Health.Use));
  const facilityId = useFacilityStore((s) => s.facilityId);
  const { data } = useMyAccess(facilityId, on && uses);
  return new Set(data?.roles ?? []);
}
